import { useEffect, useMemo, useState } from "react";
import { Puck, type Data } from "@puckeditor/core";
import "@puckeditor/core/puck.css";
import {
  buildBlogPublishOutputPlan,
  countEnabledBlogPublishOutputs,
} from "@ease/page-builder/blog";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  FileText,
  Image,
  Loader2,
  Save,
  Send,
  Tag,
} from "lucide-react";
import {
  createBlogCategory,
  createBlogSeries,
  createBlogPost,
  createBlogPostWithMetadata,
  publishBlogPost,
  scheduleBlogPost,
  updateBlogPost,
  useBlogCategories,
  useBlogPerformance,
  useBlogPost,
  useBlogSeries,
  useBlogSites,
} from "@/hooks/use-blog";
import { savePage, usePageDetail } from "@/hooks/use-pages";
import { blogPuckConfig, normalizeBlogPuckData } from "./blog-puck-config";
import { scoreSeo } from "@/lib/seo-scorer";
import { optimizeSeoDraft, type PuckComponent, type SeoMeta } from "@/lib/seo-engine";
import { BlogSeoScoreCard } from "./blog-seo-score-card";
import PageAnalyticsPanel from "@/components/pages/page-analytics-panel";

const COMPONENT_SEO_FIX_IDS = new Set(["seo:alt", "seo:headings"]);

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function BlogEditorPage() {
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !postId || postId === "new";
  const siteId = searchParams.get("siteId") ?? "";

  const { sites, isLoading: isLoadingSites } = useBlogSites();
  const { categories, isLoading: isLoadingCategories, refetch: refetchCategories } = useBlogCategories(siteId || undefined);
  const { series, isLoading: isLoadingSeries, refetch: refetchSeries } = useBlogSeries(siteId || undefined);
  const { post, isLoading: isLoadingPost } = useBlogPost(siteId || undefined, isNew ? undefined : postId);
  const { performance, isLoading: isLoadingPerformance } = useBlogPerformance(siteId || undefined, isNew ? undefined : postId);
  const { page: bodyPage, isLoading: isLoadingBody, error: bodyError } = usePageDetail(post?.pageId);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [featuredImage, setFeaturedImage] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState("");
  const [seriesOrder, setSeriesOrder] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [focusKeyword, setFocusKeyword] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [sendToSubscribersOnPublish, setSendToSubscribersOnPublish] = useState(true);
  const [createSocialDraftOnPublish, setCreateSocialDraftOnPublish] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newSeriesTitle, setNewSeriesTitle] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [isCreatingSeries, setIsCreatingSeries] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sidebarTab, setSidebarTab] = useState<"seo" | "performance">("seo");
  const [bodyData, setBodyData] = useState<Data>({ root: {}, content: [] });
  const [bodyIsDirty, setBodyIsDirty] = useState(false);
  const [unsupportedBodyBlocks, setUnsupportedBodyBlocks] = useState<string[]>([]);
  const [seoOptimizationStatus, setSeoOptimizationStatus] = useState<string | null>(null);

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === siteId) ?? null,
    [siteId, sites],
  );

  useEffect(() => {
    if (!post) return;

    setTitle(post.title);
    setSlug(post.slug);
    setExcerpt(post.excerpt ?? "");
    setFeaturedImage(post.featuredImage ?? "");
    setAuthorName(post.authorName);
    setSelectedCategories((post.categories ?? []).map((category) => category.id));
    setSelectedSeriesId(post.seriesId ?? "");
    setSeriesOrder(post.seriesOrder ? String(post.seriesOrder) : "");
    setSeoTitle(String(post.seoMeta?.title ?? post.title));
    setSeoDescription(String(post.seoMeta?.description ?? post.excerpt ?? ""));
    setFocusKeyword(String(post.seoMeta?.focusKeyword ?? ""));
    setCanonicalUrl(post.canonicalUrl ?? "");
    setScheduledFor(post.scheduledFor ? post.scheduledFor.slice(0, 16) : "");
    setSendToSubscribersOnPublish(post.sendToSubscribersOnPublish ?? true);
    setCreateSocialDraftOnPublish(post.createSocialDraftOnPublish ?? false);
  }, [post]);

  useEffect(() => {
    if (isNew && title && !slug) {
      setSlug(slugify(title));
    }
  }, [isNew, slug, title]);

  useEffect(() => {
    if (isNew) {
      setBodyData({ root: {}, content: [] });
      setUnsupportedBodyBlocks([]);
      return;
    }

    if (!post || !bodyPage) {
      return;
    }

    const normalized = normalizeBlogPuckData(bodyPage.content);
    setBodyData(normalized.data);
    setUnsupportedBodyBlocks(normalized.unsupportedTypes);
    setBodyIsDirty(false);
  }, [bodyPage, isNew, post]);

  const metadataPayload = {
    title: title.trim(),
    slug: slugify(slug || title),
    excerpt: excerpt.trim() || undefined,
    featuredImage: featuredImage.trim() || undefined,
    authorName: authorName.trim() || undefined,
    categoryIds: selectedCategories,
    seriesId: selectedSeriesId || null,
    seriesOrder: selectedSeriesId ? Number(seriesOrder || "0") || null : null,
    seoMeta: {
      title: seoTitle.trim() || title.trim(),
      description: seoDescription.trim() || excerpt.trim(),
      focusKeyword: focusKeyword.trim(),
    },
    canonicalUrl: canonicalUrl.trim() || null,
    sendToSubscribersOnPublish,
    createSocialDraftOnPublish,
  };

  const seoScore = useMemo(
    () => scoreSeo(
      bodyData,
      seoTitle.trim() || title.trim(),
      slugify(slug || title),
      focusKeyword.trim(),
      seoDescription.trim() || excerpt.trim(),
    ),
    [bodyData, excerpt, focusKeyword, seoDescription, seoTitle, slug, title],
  );

  const publishOutputs = useMemo(() => buildBlogPublishOutputPlan({
    isPublished: post?.status === "published",
    sendToSubscribersOnPublish,
    newsletterSent: post?.newsletterSent ?? false,
    createSocialDraftOnPublish,
    socialDraftPostId: post?.socialDraftPostId ?? null,
  }), [
    createSocialDraftOnPublish,
    post?.newsletterSent,
    post?.socialDraftPostId,
    post?.status,
    sendToSubscribersOnPublish,
  ]);
  const enabledPublishOutputCount = countEnabledBlogPublishOutputs(publishOutputs);

  const persistPost = async () => {
    if (!siteId || !title.trim()) {
      throw new Error("missing_site_or_title");
    }

    if (isNew) {
      const updated = await createBlogPostWithMetadata(siteId, metadataPayload);
      navigate(`/cms/blog/${updated.id}/edit?siteId=${siteId}`, { replace: true });
      return updated;
    }

    return updateBlogPost(siteId, postId!, {
      title: metadataPayload.title,
      slug: metadataPayload.slug,
      excerpt: metadataPayload.excerpt ?? null,
      featuredImage: metadataPayload.featuredImage ?? null,
      authorName: metadataPayload.authorName,
      categoryIds: metadataPayload.categoryIds,
      seriesId: metadataPayload.seriesId,
      seriesOrder: metadataPayload.seriesOrder,
      seoMeta: metadataPayload.seoMeta,
      canonicalUrl: metadataPayload.canonicalUrl,
      sendToSubscribersOnPublish: metadataPayload.sendToSubscribersOnPublish,
      createSocialDraftOnPublish: metadataPayload.createSocialDraftOnPublish,
    });
  };

  const persistBody = async (pageId: string) => {
    if (unsupportedBodyBlocks.length > 0) {
      return;
    }

    await savePage(pageId, bodyData);
    setBodyIsDirty(false);
  };

  const handleAutoOptimizeSeo = () => {
    const componentFixesAllowed = unsupportedBodyBlocks.length === 0;
    const optimization = optimizeSeoDraft(
      {
        title: seoTitle.trim() || title.trim(),
        description: seoDescription.trim() || excerpt.trim(),
        ogImage: featuredImage.trim(),
        slug: slugify(slug || title),
        focusKeyword: focusKeyword.trim(),
        canonicalUrl: canonicalUrl.trim() || undefined,
      } satisfies SeoMeta,
      (bodyData.content ?? []) as PuckComponent[],
      { includeComponentFixes: componentFixesAllowed },
    );

    setSeoTitle(optimization.meta.title || title.trim());
    setSeoDescription(optimization.meta.description || seoDescription);
    setFocusKeyword(optimization.meta.focusKeyword || focusKeyword);
    setSlug(optimization.meta.slug || slugify(slug || title));
    if (optimization.meta.ogImage) {
      setFeaturedImage(optimization.meta.ogImage);
    }

    const appliedComponentFix = optimization.applied.some((item) => (
      COMPONENT_SEO_FIX_IDS.has(item.recommendationId)
    ));
    if (componentFixesAllowed && appliedComponentFix) {
      setBodyData((current) => ({
        ...current,
        content: optimization.components as Data["content"],
      }));
      setBodyIsDirty(true);
    }

    setSeoOptimizationStatus(
      optimization.applied.length === 0
        ? "No deterministic SEO fixes are available for this blog draft."
        : `Applied ${optimization.applied.length} SEO fix${optimization.applied.length === 1 ? "" : "es"} to metadata and body content.`,
    );
  };

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);

    try {
      const saved = await persistPost();
      await persistBody(saved.pageId);
    } catch {
      setError("Failed to save the blog post.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    setError(null);
    setIsSaving(true);

    try {
      const saved = await persistPost();
      await persistBody(saved.pageId);
      await publishBlogPost(siteId, saved.id);
      navigate(`/cms/blog?siteId=${siteId}`);
    } catch {
      setError("Failed to publish the blog post.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduledFor) {
      setError("Choose a future date before scheduling.");
      return;
    }

    setError(null);
    setIsSaving(true);

    try {
      const saved = await persistPost();
      await persistBody(saved.pageId);
      await scheduleBlogPost(siteId, saved.id, new Date(scheduledFor).toISOString());
      navigate(`/cms/blog?siteId=${siteId}`);
    } catch {
      setError("Failed to schedule the blog post.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!siteId || !name) {
      setError("Enter a category name before creating it.");
      return;
    }

    setError(null);
    setIsCreatingCategory(true);

    try {
      const category = await createBlogCategory(siteId, name);
      await refetchCategories();
      setSelectedCategories((current) => (
        current.includes(category.id) ? current : [...current, category.id]
      ));
      setNewCategoryName("");
    } catch {
      setError("Failed to create the category.");
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const handleCreateSeries = async () => {
    const title = newSeriesTitle.trim();
    if (!siteId || !title) {
      setError("Enter a series title before creating it.");
      return;
    }

    setError(null);
    setIsCreatingSeries(true);

    try {
      const entry = await createBlogSeries(siteId, { title });
      await refetchSeries();
      setSelectedSeriesId(entry.id);
      setSeriesOrder((current) => current || "1");
      setNewSeriesTitle("");
    } catch {
      setError("Failed to create the blog series.");
    } finally {
      setIsCreatingSeries(false);
    }
  };

  if (isLoadingSites || (!isNew && isLoadingPost)) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!siteId || !selectedSite) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-10 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]/50" />
        <h1 className="mt-4 text-xl font-semibold text-[var(--color-foreground)]">Pick a site before editing a blog post</h1>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          The mounted blog contract is site-scoped, so this editor only opens with a concrete `siteId`.
        </p>
        <Link
          to="/cms/blog"
          className="mt-5 inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
        >
          Back to blog workspace
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Link
            to={`/cms/blog?siteId=${siteId}`}
            className="inline-flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to blog workspace
          </Link>
          <h1 className="mt-3 text-2xl font-semibold text-[var(--color-foreground)]">
            {isNew ? "Create blog post" : "Edit blog post"}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Site: {selectedSite.name}. Metadata and article body now share this mounted editor surface.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {post && (
            <Link
              to={`/pages/editor/${post.pageId}?siteId=${siteId}&entry=blog`}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
            >
              <ExternalLink className="h-4 w-4" />
              Open full page editor
            </Link>
          )}
          <button
            type="button"
            onClick={() => {
              void handleSave();
            }}
            disabled={isSaving || !title.trim()}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] disabled:opacity-60"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              void handlePublish();
            }}
            disabled={isSaving || !title.trim()}
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            style={{ background: "var(--gradient-energy)" }}
          >
            <Send className="h-4 w-4" />
            Publish
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section className="space-y-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Article body</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Draft the post with constrained content blocks on the same CMS route. Use the full page editor only for legacy layouts or richer landing-page style compositions.
                </p>
              </div>
              {bodyIsDirty ? (
                <span className="rounded-full bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-700">
                  Unsaved body changes
                </span>
              ) : null}
            </div>

            <div className="mt-4">
              {!isNew && isLoadingBody ? (
                <div className="flex min-h-[240px] items-center justify-center rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-card)]">
                  <Loader2 className="h-5 w-5 animate-spin text-[var(--color-primary)]" />
                </div>
              ) : bodyError ? (
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-700">
                  The linked page content could not be loaded. Open the full page editor once `/crm/pages` recovers.
                </div>
              ) : unsupportedBodyBlocks.length > 0 ? (
                <div className="space-y-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                  <p className="text-sm text-[var(--color-foreground)]">
                    This post still contains legacy layout blocks outside the constrained blog schema:
                    {" "}
                    <span className="font-medium">{unsupportedBodyBlocks.join(", ")}</span>.
                  </p>
                  <p className="text-sm text-[var(--color-muted-foreground)]">
                    To avoid discarding those blocks, keep editing this post in the full page editor until it has been simplified into article blocks.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-[var(--color-border)] bg-white">
                  <Puck
                    config={blogPuckConfig}
                    data={bodyData}
                    onPublish={() => undefined}
                    onChange={(nextData) => {
                      setBodyData(nextData);
                      setBodyIsDirty(true);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <label className="block space-y-2">
            <span className="text-sm font-medium text-[var(--color-foreground)]">Title</span>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Give the post a strong, search-friendly title"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-medium text-[var(--color-foreground)]">Excerpt</span>
            <textarea
              value={excerpt}
              onChange={(event) => setExcerpt(event.target.value)}
              rows={4}
              placeholder="Short summary for cards, feeds, and search previews"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-medium text-[var(--color-foreground)]">Author</span>
            <input
              value={authorName}
              onChange={(event) => setAuthorName(event.target.value)}
              placeholder="Defaults to Author when left blank"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
            />
          </label>

          <div className="grid gap-5 md:grid-cols-2">
            <label className="block space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">Slug</span>
              <input
                value={slug}
                onChange={(event) => setSlug(event.target.value)}
                placeholder="my-post-slug"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
              />
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">Canonical URL</span>
              <input
                value={canonicalUrl}
                onChange={(event) => setCanonicalUrl(event.target.value)}
                placeholder="https://example.com/blog/post"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
              />
            </label>
          </div>

          <label className="flex items-start gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-4">
            <input
              type="checkbox"
              checked={sendToSubscribersOnPublish}
              onChange={(event) => setSendToSubscribersOnPublish(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]/30"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-[var(--color-foreground)]">
                Send to subscribers on publish
              </span>
              <span className="block text-xs text-[var(--color-muted-foreground)]">
                When enabled, the existing `blog.post_published` event will queue the mounted subscriber newsletter flow after publish.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-4">
            <input
              type="checkbox"
              checked={createSocialDraftOnPublish}
              onChange={(event) => setCreateSocialDraftOnPublish(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]/30"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-[var(--color-foreground)]">
                Create social draft on publish
              </span>
              <span className="block text-xs text-[var(--color-muted-foreground)]">
                When enabled, the mounted `blog.post_published` event will create a linked social draft for manual review on the existing social post seam.
              </span>
            </span>
          </label>

          <label className="block space-y-2">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-foreground)]">
              <Image className="h-4 w-4" />
              Featured image
            </span>
            <input
              value={featuredImage}
              onChange={(event) => setFeaturedImage(event.target.value)}
              placeholder="https://..."
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
            />
          </label>

          <label className="block space-y-2">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-foreground)]">
              <Tag className="h-4 w-4" />
              Categories
            </span>
            {isLoadingCategories ? (
              <div className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-3 text-sm text-[var(--color-muted-foreground)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading categories
              </div>
            ) : (
              <div className="grid gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4 sm:grid-cols-2">
                {categories.length === 0 ? (
                  <p className="text-sm text-[var(--color-muted-foreground)]">No categories yet for this site. Create the first one here without leaving the editor.</p>
                ) : (
                  categories.map((category) => (
                    <label key={category.id} className="flex items-center gap-2 text-sm text-[var(--color-foreground)]">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category.id)}
                        onChange={(event) => {
                          setSelectedCategories((current) => (
                            event.target.checked
                              ? [...current, category.id]
                              : current.filter((value) => value !== category.id)
                          ));
                        }}
                      />
                      {category.name}
                    </label>
                  ))
                )}
              </div>
            )}
            <div className="flex flex-col gap-2 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-3 sm:flex-row sm:items-center">
              <input
                value={newCategoryName}
                onChange={(event) => setNewCategoryName(event.target.value)}
                placeholder="Create category"
                className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
              <button
                type="button"
                onClick={() => {
                  void handleCreateCategory();
                }}
                disabled={isCreatingCategory || !newCategoryName.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:opacity-60"
              >
                {isCreatingCategory ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />}
                Add category
              </button>
            </div>
          </label>

          <section className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <div>
              <h2 className="text-sm font-medium text-[var(--color-foreground)]">Series</h2>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                Assign this post to a multipart series and control its order in the reading sequence.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_120px]">
              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                  Series
                </span>
                <select
                  value={selectedSeriesId}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setSelectedSeriesId(nextValue);
                    if (!nextValue) {
                      setSeriesOrder("");
                    } else if (!seriesOrder) {
                      setSeriesOrder("1");
                    }
                  }}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                >
                  <option value="">{isLoadingSeries ? "Loading series…" : "No series"}</option>
                  {series.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.title}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                  Part
                </span>
                <input
                  type="number"
                  min={1}
                  value={seriesOrder}
                  onChange={(event) => setSeriesOrder(event.target.value)}
                  placeholder="1"
                  disabled={!selectedSeriesId}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:opacity-60"
                />
              </label>
            </div>

            <div className="flex flex-col gap-2 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-3 sm:flex-row sm:items-center">
              <input
                value={newSeriesTitle}
                onChange={(event) => setNewSeriesTitle(event.target.value)}
                placeholder={isLoadingSeries ? "Loading series…" : "Create series"}
                className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
              <button
                type="button"
                onClick={() => {
                  void handleCreateSeries();
                }}
                disabled={isCreatingSeries || !newSeriesTitle.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:opacity-60"
              >
                {isCreatingSeries ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                Add series
              </button>
            </div>
          </section>
        </section>

        <aside className="space-y-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Runtime boundary</p>
            <p className="mt-2 text-sm text-[var(--color-foreground)]">
              This editor owns the mounted metadata, page-builder body, deterministic SEO optimizer, and publish fan-out contract. Comment moderation runs through the CMS detail view and purchase-intent comments are routed to CRM by the event-router target.
            </p>
          </div>

          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                  1 publish → {enabledPublishOutputCount} outputs
                </p>
                <p className="mt-2 text-sm text-[var(--color-foreground)]">
                  Publish saves the blog metadata/body, starts the site artifact publish job, regenerates blog discovery, and emits subscriber/social automation events.
                </p>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {publishOutputs.map((output) => (
                <div
                  key={output.id}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-[var(--color-foreground)]">{output.label}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                      output.status === "complete"
                        ? "bg-emerald-500/10 text-emerald-700"
                        : output.status === "disabled"
                          ? "bg-slate-500/10 text-slate-600"
                          : "bg-blue-500/10 text-blue-700"
                    }`}>
                      {output.status}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{output.description}</p>
                  <p className="mt-2 font-mono text-[10px] text-[var(--color-muted-foreground)]">{output.contract}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-1">
            {([
              { id: "seo", label: "SEO", icon: FileText },
              { id: "performance", label: "Performance", icon: Tag },
            ] as const).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setSidebarTab(tab.id)}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm transition ${
                    sidebarTab === tab.id
                      ? "bg-[var(--color-primary)]/10 font-medium text-[var(--color-primary)]"
                      : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>

          {sidebarTab === "seo" ? (
            <>
              <div className="space-y-2">
                <h2 className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
                  <FileText className="h-4 w-4" />
                  SEO handoff
                </h2>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  Publish still runs through the mounted blog contract. This panel now scores the metadata and article body that contract accepts today.
                </p>
              </div>

              <BlogSeoScoreCard score={seoScore} />

              <button
                type="button"
                onClick={handleAutoOptimizeSeo}
                disabled={!title.trim()}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-2.5 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)] disabled:opacity-60"
              >
                Auto-optimize SEO
              </button>

              {seoOptimizationStatus ? (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700">
                  {seoOptimizationStatus}
                </div>
              ) : null}

              <label className="block space-y-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">SEO title</span>
                <input
                  value={seoTitle}
                  onChange={(event) => setSeoTitle(event.target.value)}
                  placeholder="Defaults to post title"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                />
              </label>

              <label className="block space-y-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">SEO description</span>
                <textarea
                  value={seoDescription}
                  onChange={(event) => setSeoDescription(event.target.value)}
                  rows={4}
                  placeholder="Defaults to the excerpt"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                />
              </label>

              <label className="block space-y-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">Focus keyword</span>
                <input
                  value={focusKeyword}
                  onChange={(event) => setFocusKeyword(event.target.value)}
                  placeholder="e.g. ai email automation"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                />
              </label>

              <label className="block space-y-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">Schedule publish</span>
                <input
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(event) => setScheduledFor(event.target.value)}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                />
              </label>

              <button
                type="button"
                onClick={() => {
                  void handleSchedule();
                }}
                disabled={isSaving || !title.trim() || !scheduledFor}
                className="w-full rounded-lg border border-[var(--color-border)] px-4 py-2.5 text-sm text-[var(--color-foreground)] disabled:opacity-60"
              >
                Schedule via mounted API
              </button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <h2 className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
                  <Tag className="h-4 w-4" />
                  Performance
                </h2>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  Performance uses the published page-session telemetry from the backing page plus source-url attribution from captured lead submissions.
                </p>
              </div>

              {!post?.pageId ? (
                <div className="rounded-xl bg-[var(--color-muted)]/50 p-4 text-sm text-[var(--color-muted-foreground)]">
                  Save this draft once to create its backing page before performance analytics can attach.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                        Leads From This Post
                      </p>
                      <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
                        {isLoadingPerformance ? "…" : (performance?.attributedLeads ?? 0)}
                      </p>
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                        Distinct leads attributed from captured blog `source_url` truth.
                      </p>
                    </div>

                    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                        Attributed Submissions
                      </p>
                      <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
                        {isLoadingPerformance ? "…" : (performance?.attributedSubmissions ?? 0)}
                      </p>
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                        Form submissions captured from this post path.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]">
                    <PageAnalyticsPanel pageId={post.pageId} />
                  </div>
                </>
              )}
            </div>
          )}

          {post?.pageId ? (
            <div className="rounded-xl bg-[var(--color-muted)]/50 p-4 text-sm text-[var(--color-muted-foreground)]">
              The article body lives on page <span className="font-mono text-xs text-[var(--color-foreground)]">{post.pageId}</span>.
              {" "}Open the page editor to change sections, layouts, and published content.
            </div>
          ) : (
            <div className="rounded-xl bg-[var(--color-muted)]/50 p-4 text-sm text-[var(--color-muted-foreground)]">
              Save this draft once to create its backing page. The page editor link appears immediately after creation.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
